import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Switch,
    TextField,
    Button,
    Grid,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import {
    Headset as SupportIcon,
    Phone as BookingIcon,
    Mic as VoiceIcon,
    Settings as SettingsIcon,
    Bolt as ElectricIcon,
    Chat as ChatIcon,
    WhatsApp as WhatsAppIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Star as StarIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import appConfig from "../config.json";

const AiSettings = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updatingIntegrations, setUpdatingIntegrations] = useState(new Set());

    // WhatsApp Modal States
    const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
    const [whatsappStatus, setWhatsappStatus] = useState(null);
    const [whatsappLoading, setWhatsappLoading] = useState(false);
    const [whatsappError, setWhatsappError] = useState(null);
    const [qrCode, setQrCode] = useState(null);
    const [showQr, setShowQr] = useState(false);
    const [qrLoading, setQrLoading] = useState(false);
    const [statusPolling, setStatusPolling] = useState(null);
    const [qrTimeout, setQrTimeout] = useState(null);
    const [isQrTimeout, setIsQrTimeout] = useState(false);

    // Subscription Modal States
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [subscriptionPackages, setSubscriptionPackages] = useState([]);
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);
    const [subscriptionError, setSubscriptionError] = useState(null);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [customerEmail, setCustomerEmail] = useState("");
    const [customerName, setCustomerName] = useState("");

    // Token Balance States
    const [tokenBalance, setTokenBalance] = useState(null);
    const [tokenBalanceLoading, setTokenBalanceLoading] = useState(false);
    const [tokenBalanceError, setTokenBalanceError] = useState(null);

    // Fetch integrations data from API
    const fetchIntegrations = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(
                `${appConfig.api_endpoints.whatsapp_ai_url}/integrations`,
                {
                    method: "GET",
                    headers: {
                        "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setIntegrations(data);
        } catch (err) {
            console.error("Error fetching integrations:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchIntegrations();
    }, []);

    // Fetch Token Balance
    const fetchTokenBalance = async () => {
        try {
            setTokenBalanceLoading(true);
            setTokenBalanceError(null);

            const clientId = 5; // Static client ID
            const response = await fetch(
                `https://dashboardai.aiagentx.ai/api/tokens/balance?client_id=${clientId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (data.success) {
                setTokenBalance(data.data);
            } else {
                throw new Error("Failed to fetch token balance");
            }
        } catch (err) {
            console.error("Error fetching token balance:", err);
            setTokenBalanceError(err.message);
        } finally {
            setTokenBalanceLoading(false);
        }
    };

    // Fetch token balance when switching to Tokens & Costs tab
    useEffect(() => {
        if (activeTab === 1) {
            fetchTokenBalance();
        }
    }, [activeTab]);

    // Check WhatsApp Status
    const checkWhatsAppStatus = async () => {
        try {
            setWhatsappError(null);

            if (isQrTimeout) {
                return;
            }

            const response = await fetch(
                `${appConfig.api_endpoints.whatsapp_ai_url}/wa/status`,
                {
                    method: "GET",
                    headers: {
                        "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setWhatsappStatus(data);

            // If connected, stop polling and hide QR
            if (data.connected) {
                stopStatusPolling();
                setShowQr(false);
                setQrCode(null);
            }

            // startStatusPolling();
            return data;
        } catch (err) {
            console.error("Error checking WhatsApp status:", err);
            setWhatsappError(err.message);
            return null;
        } finally {
            setWhatsappLoading(false);
        }
    };

    // Fetch QR Code
    const fetchQrCode = async () => {
        try {
            setWhatsappLoading(true);
            setWhatsappError(null);

            const response = await fetch(
                `${appConfig.api_endpoints.whatsapp_ai_url}/wa/qr`,
                {
                    method: "GET",
                    headers: {
                        "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const imageUrl = URL.createObjectURL(blob);
            setQrCode(imageUrl);
            setShowQr(true);
            setIsQrTimeout(false);
            setWhatsappLoading(false);

            const timeout = setTimeout(() => {
                setIsQrTimeout(true);
            }, 120000);

            setQrTimeout(timeout);
        } catch (err) {
            console.error("Error fetching QR code:", err);
            setWhatsappError(err.message);
        } finally {
            setWhatsappLoading(false);
        }
    };

    // Start Status Polling
    const startStatusPolling = () => {
        const interval = setInterval(async () => {
            const status = await checkWhatsAppStatus();
            if (status && status.connected) {
                clearInterval(interval);
                setStatusPolling(null);
                // Clear timeout when connected
                if (qrTimeout) {
                    clearTimeout(qrTimeout);
                    setQrTimeout(null);
                }
            }
        }, 5000);

        setStatusPolling(interval);
    };

    // Stop Status Polling
    const stopStatusPolling = () => {
        if (statusPolling) {
            clearInterval(statusPolling);
            setStatusPolling(null);
        }
        if (qrTimeout) {
            clearTimeout(qrTimeout);
            setQrTimeout(null);
        }
    };

    // Reload QR Code
    const reloadQrCode = async () => {
        // Clear existing timeout
        if (qrTimeout) {
            clearTimeout(qrTimeout);
            setQrTimeout(null);
        }

        // Clear existing QR
        setQrCode(null);

        // check status
        await checkWhatsAppStatus();

        // Fetch new QR code
        await fetchQrCode();
    };

    // Handle WhatsApp Modal Open
    const handleWhatsAppModalOpen = async () => {
        setWhatsappModalOpen(true);
        setWhatsappLoading(true);

        const status = await checkWhatsAppStatus();

        // If disconnected, immediately fetch QR code and start polling
        if (status && !status.connected) {
            await fetchQrCode();
            // startStatusPolling();
            await checkWhatsAppStatus();
        }
    };

    // Handle WhatsApp Modal Close
    const handleWhatsAppModalClose = () => {
        setWhatsappModalOpen(false);
        setWhatsappStatus(null);
        setWhatsappError(null);
        setShowQr(false);
        setQrCode(null);
        stopStatusPolling();
    };

    // Cleanup on component unmount
    useEffect(() => {
        return () => {
            stopStatusPolling();
            if (qrCode) {
                URL.revokeObjectURL(qrCode);
            }
        };
    }, [qrCode]);

    // Handle Logout WhatsApp
    const handleLogoutWhatsApp = async () => {
        try {
            setWhatsappLoading(true);
            setWhatsappError(null);

            const response = await fetch(
                `${appConfig.api_endpoints.whatsapp_ai_url}/wa/logout`,
                {
                    method: "GET",
                    headers: {
                        "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log("WhatsApp logout response:", data);

            // After successful logout, fetch QR code and start polling
            await checkWhatsAppStatus();
            await fetchQrCode();
        } catch (err) {
            console.error("Error logging out WhatsApp:", err);
            setWhatsappError(err.message);
        } finally {
            setWhatsappLoading(false);
        }
    };

    // Fetch Subscription Packages
    const fetchSubscriptionPackages = async () => {
        try {
            setSubscriptionLoading(true);
            setSubscriptionError(null);

            const response = await fetch(
                "https://dashboardai.aiagentx.ai/api/subscription/packages",
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (data.success) {
                setSubscriptionPackages(data.data);
            } else {
                throw new Error("Failed to fetch subscription packages");
            }
        } catch (err) {
            console.error("Error fetching subscription packages:", err);
            setSubscriptionError(err.message);
        } finally {
            setSubscriptionLoading(false);
        }
    };

    // Handle Subscription Modal Open
    const handleSubscriptionModalOpen = async () => {
        setSubscriptionModalOpen(true);
        await fetchSubscriptionPackages();

        // Check payment status from URL params
        const params = new URLSearchParams(window.location.search);
        const success = params.get("success");
        const canceled = params.get("canceled");
        const sessionId = params.get("session_id");

        if (success) {
            alert("Payment successful! 🎉 Your subscription is now active.");
            // Remove success param from URL
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
        } else if (canceled) {
            alert("Payment was canceled.");
            // Remove canceled param from URL
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
        } else if (sessionId) {
            checkPaymentStatus(sessionId);
        }
    };

    // Check Payment Status
    const checkPaymentStatus = async (sessionId) => {
        try {
            const response = await fetch(
                `https://dashboardai.aiagentx.ai/api/stripe/session/${sessionId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (data.success && data.data.paymentStatus === "paid") {
                alert("Payment successful! Your subscription is now active.");
                // Remove session_id from URL
                const newUrl = window.location.pathname;
                window.history.replaceState({}, document.title, newUrl);
            }
        } catch (err) {
            console.error("Error checking payment status:", err);
        }
    };

    // Handle Subscription Modal Close
    const handleSubscriptionModalClose = () => {
        setSubscriptionModalOpen(false);
        setSubscriptionError(null);
        setPaymentLoading(false);
        setCustomerEmail("");
        setCustomerName("");
    };

    // Handle Subscribe to Package with Stripe (No Email Required)
    const handleSubscribe = async (packageData) => {
        if (!packageData || !packageData.id) {
            alert("Invalid package selected");
            return;
        }

        try {
            setPaymentLoading(true);

            // Static userId from firebase-demo-users.json (admin demo)
            const userId = "demo_admin_panama_001";

            const requestBody = {
                packageId: packageData.id,
                clientId: 5, // Add userId to request body
                // Redirect URLs - balik ke halaman ini
                customerEmail: "admin@demo.com",
                customerName: "Admin Demo",
                successUrl: `${
                    window.location.href.split("?")[0]
                }?success=true`,
                cancelUrl: `${
                    window.location.href.split("?")[0]
                }?canceled=true`,
            };

            // Email dan name optional - kalau diisi akan pre-fill di Stripe
            if (customerEmail) {
                requestBody.customerEmail = customerEmail;
            }
            if (customerName) {
                requestBody.customerName = customerName;
            }

            const response = await fetch(
                "https://dashboardai.aiagentx.ai/api/stripe/checkout",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success && data.data.url) {
                // Redirect to Stripe Checkout
                window.location.href = data.data.url;
            } else {
                throw new Error(
                    data.error || "Failed to create checkout session"
                );
            }
        } catch (err) {
            console.error("Error creating checkout session:", err);
            alert(`Payment error: ${err.message}`);
            setPaymentLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleSwitchChange = async (integrationId, checked) => {
        // Find the current integration to get its webhook_url
        const currentIntegration = integrations.find(
            (int) => int.id === integrationId
        );
        if (!currentIntegration) return;

        try {
            // Add to updating set
            setUpdatingIntegrations((prev) => new Set(prev).add(integrationId));

            // Update local state immediately for better UX
            setIntegrations((prev) =>
                prev.map((integration) =>
                    integration.id === integrationId
                        ? { ...integration, status: checked }
                        : integration
                )
            );

            // Make API call to update integration status
            const response = await fetch(
                `${appConfig.api_endpoints.whatsapp_ai_url}/integrations/${integrationId}`,
                {
                    method: "PUT",
                    headers: {
                        "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        status: checked,
                        webhook_url: currentIntegration.webhook_url || "",
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            console.log(
                `Successfully updated integration ${integrationId} status to ${checked}`
            );
        } catch (err) {
            console.error("Error updating integration status:", err);
            // Revert local state on error
            setIntegrations((prev) =>
                prev.map((integration) =>
                    integration.id === integrationId
                        ? { ...integration, status: !checked }
                        : integration
                )
            );
            // Show error to user
            setError(
                `Failed to update ${currentIntegration.name}: ${err.message}`
            );
        } finally {
            // Remove from updating set
            setUpdatingIntegrations((prev) => {
                const newSet = new Set(prev);
                newSet.delete(integrationId);
                return newSet;
            });
        }
    };

    const handleInputChange = (integrationId, field, value) => {
        setIntegrations((prev) =>
            prev.map((integration) =>
                integration.id === integrationId
                    ? { ...integration, [field]: value }
                    : integration
            )
        );
    };

    const handleTestConnection = (integration) => {
        console.log(`Testing connection for ${integration.name}`, integration);
        // Implement test connection logic
    };

    const handleSaveConfig = async (integration) => {
        try {
            // Add to updating set
            setUpdatingIntegrations((prev) =>
                new Set(prev).add(integration.id)
            );

            console.log(`Saving config for ${integration.name}`, integration);

            // Make API call to save integration config
            const response = await fetch(
                `${appConfig.api_endpoints.whatsapp_ai_url}/integrations/${integration.id}`,
                {
                    method: "PUT",
                    headers: {
                        "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        webhook_url: integration.webhook_url || "",
                        status: integration.status,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            console.log(`Successfully saved config for ${integration.name}`);

            // Clear any existing errors
            setError(null);

            // Optionally show success message
            // You could add a success state here if needed
        } catch (err) {
            console.error("Error saving integration config:", err);
            setError(
                `Failed to save ${integration.name} config: ${err.message}`
            );
        } finally {
            // Remove from updating set
            setUpdatingIntegrations((prev) => {
                const newSet = new Set(prev);
                newSet.delete(integration.id);
                return newSet;
            });
        }
    };

    const ServiceCard = ({ integration, icon, description }) => {
        if (!integration) return null;

        const isUpdating = updatingIntegrations.has(integration.id);

        return (
            <Grid item xs={12} md={4}>
                <Card
                    sx={{
                        height: "100%",
                        boxShadow: "none",
                        border: "1px solid #e0e0e0",
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 3,
                            }}
                        >
                            <Box sx={{ mr: 2, color: "#1976d2" }}>{icon}</Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: "bold", mb: 0.5 }}
                                >
                                    {integration.name}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    {description}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    mb: 2,
                                }}
                            >
                                <Typography variant="body1" fontWeight="medium">
                                    {t("connection_status")}
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                    }}
                                >
                                    <Chip
                                        label={
                                            integration.status
                                                ? t("active")
                                                : t("inactive")
                                        }
                                        color={
                                            integration.status
                                                ? "success"
                                                : "error"
                                        }
                                        size="small"
                                    />
                                    <Switch
                                        checked={integration.status}
                                        onChange={(e) =>
                                            handleSwitchChange(
                                                integration.id,
                                                e.target.checked
                                            )
                                        }
                                        color="primary"
                                        disabled={isUpdating}
                                    />
                                    {isUpdating && (
                                        <CircularProgress
                                            size={16}
                                            sx={{ ml: 1 }}
                                        />
                                    )}
                                </Box>
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                {integration.status
                                    ? t("connected_to_workflow")
                                    : t("disconnected")}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography
                                variant="body2"
                                fontWeight="medium"
                                sx={{ mb: 1 }}
                            >
                                {t("webhook_url")}
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                value={
                                    integration.webhook_url
                                        ? "**********************"
                                        : ""
                                }
                                onChange={(e) =>
                                    handleInputChange(
                                        integration.id,
                                        "webhook_url",
                                        e.target.value
                                    )
                                }
                                disabled={true}
                                sx={{ mb: 2 }}
                                placeholder="Webhook URL is hidden for security"
                                helperText="Webhook URL is hidden for security reasons"
                            />
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "space-between",
                            }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() =>
                                    handleTestConnection(integration)
                                }
                                disabled={!integration.status || isUpdating}
                                sx={{ flex: 1 }}
                            >
                                {t("test_connection")}
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleSaveConfig(integration)}
                                disabled={!integration.status || isUpdating}
                                sx={{ flex: 1 }}
                                startIcon={
                                    isUpdating ? (
                                        <CircularProgress size={16} />
                                    ) : null
                                }
                            >
                                {isUpdating
                                    ? t("saving") || "Saving..."
                                    : t("save_config")}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ mb: 1, fontWeight: "bold" }}>
                    {t("ai_configuration")}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    {t("ai_configuration_description")}
                </Typography>
            </Box>

            {/* Custom Tabs */}
            <Box
                sx={{
                    mb: 3,
                    display: "flex",
                    width: "100%",
                    border: "1px solid #ddd",
                    borderRadius: 1,
                }}
            >
                <Button
                    variant={activeTab === 0 ? "contained" : "outlined"}
                    onClick={() => setActiveTab(0)}
                    sx={{
                        flex: 1,
                        py: 0.5,
                        backgroundColor: activeTab === 0 ? "white" : "#f5f5f5",
                        color: activeTab === 0 ? "#1976d2" : "#666",
                        border: "none",
                        borderRadius: 0,
                        borderTopLeftRadius: 4,
                        borderBottomLeftRadius: 4,
                        borderRight:
                            activeTab === 0 ? "none" : "1px solid #ddd",
                        "&:hover": {
                            backgroundColor:
                                activeTab === 0 ? "white" : "#e0e0e0",
                            border: "none",
                            borderRight:
                                activeTab === 0 ? "none" : "1px solid #ddd",
                        },
                    }}
                    startIcon={<SettingsIcon />}
                >
                    AI Workflows
                </Button>
                <Button
                    variant={activeTab === 1 ? "contained" : "outlined"}
                    onClick={() => setActiveTab(1)}
                    sx={{
                        flex: 1,
                        py: 0.5,
                        backgroundColor: activeTab === 1 ? "white" : "#f5f5f5",
                        color: activeTab === 1 ? "#1976d2" : "#666",
                        border: "none",
                        borderRadius: 0,
                        borderTopRightRadius: 4,
                        borderBottomRightRadius: 4,
                        borderLeft: activeTab === 1 ? "none" : "1px solid #ddd",
                        "&:hover": {
                            backgroundColor:
                                activeTab === 1 ? "white" : "#e0e0e0",
                            border: "none",
                            borderLeft:
                                activeTab === 1 ? "none" : "1px solid #ddd",
                        },
                    }}
                    startIcon={<ElectricIcon />}
                >
                    {t("tokens_costs")}
                </Button>
            </Box>

            {/* Tab Content */}
            {activeTab === 0 && (
                <Card>
                    <CardContent sx={{ p: 3 }}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1,
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: "bold" }}
                            >
                                🔗 {t("ai_workflows_integration")}
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<WhatsAppIcon />}
                                size="small"
                                sx={{
                                    backgroundColor: "#25D366",
                                    color: "white",
                                    "&:hover": {
                                        backgroundColor: "#128C7E",
                                    },
                                    textTransform: "none",
                                    fontWeight: "bold",
                                    px: 2,
                                    py: 0.5,
                                }}
                                onClick={handleWhatsAppModalOpen}
                            >
                                Configure WhatsApp
                            </Button>
                        </Box>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ mb: 3 }}
                        >
                            {t("ai_workflows_description")}
                        </Typography>

                        {loading && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    py: 4,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        )}

                        {error && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                Error loading integrations: {error}
                                <Button
                                    size="small"
                                    onClick={fetchIntegrations}
                                    sx={{ ml: 2 }}
                                >
                                    Retry
                                </Button>
                            </Alert>
                        )}

                        {!loading && !error && (
                            <Grid container spacing={3}>
                                {integrations.map((integration) => {
                                    // Map integration names to icons and descriptions
                                    let icon, description;

                                    switch (integration.name) {
                                        case "AI Support":
                                            icon = (
                                                <SupportIcon
                                                    sx={{ fontSize: 28 }}
                                                />
                                            );
                                            description = t(
                                                "ai_support_service_description"
                                            );
                                            break;
                                        case "AI Booking":
                                            icon = (
                                                <BookingIcon
                                                    sx={{ fontSize: 28 }}
                                                />
                                            );
                                            description = t(
                                                "ai_booking_service_description"
                                            );
                                            break;
                                        case "AI Voice Chat":
                                            icon = (
                                                <VoiceIcon
                                                    sx={{ fontSize: 28 }}
                                                />
                                            );
                                            description = t(
                                                "ai_voice_service_description"
                                            );
                                            break;
                                        default:
                                            icon = (
                                                <SettingsIcon
                                                    sx={{ fontSize: 28 }}
                                                />
                                            );
                                            description =
                                                "AI service integration";
                                    }

                                    return (
                                        <ServiceCard
                                            key={integration.id}
                                            integration={integration}
                                            icon={icon}
                                            description={description}
                                        />
                                    );
                                })}
                            </Grid>
                        )}
                    </CardContent>
                </Card>
            )}

            {activeTab === 1 && (
                <Card>
                    <CardContent sx={{ p: 3 }}>
                        <Typography
                            variant="h6"
                            sx={{ mb: 1, fontWeight: "bold" }}
                        >
                            💰 {t("token_usage_costs")}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ mb: 3 }}
                        >
                            {t("token_usage_description")}
                        </Typography>

                        {/* Available Tokens This Month */}
                        <Card
                            sx={{
                                mb: 3,
                                backgroundColor: "#f8f9fa",
                                borderLeft: "4px solid #007bff",
                                boxShadow: "none",
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 2,
                                    }}
                                >
                                    <Typography variant="h6" fontWeight="bold">
                                        {t("available_tokens_this_month")}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<ElectricIcon />}
                                        onClick={handleSubscriptionModalOpen}
                                    >
                                        {t("buy_tokens")}
                                    </Button>
                                </Box>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ mb: 3 }}
                                >
                                    {t("monthly_token_limit_description")}
                                </Typography>

                                {tokenBalanceLoading ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            py: 4,
                                        }}
                                    >
                                        <CircularProgress />
                                    </Box>
                                ) : tokenBalanceError ? (
                                    <Alert severity="error" sx={{ mb: 3 }}>
                                        Error loading token balance:{" "}
                                        {tokenBalanceError}
                                        <Button
                                            size="small"
                                            onClick={fetchTokenBalance}
                                            sx={{ ml: 2 }}
                                        >
                                            Retry
                                        </Button>
                                    </Alert>
                                ) : tokenBalance ? (
                                    <>
                                        <Grid
                                            container
                                            spacing={3}
                                            sx={{ mb: 3 }}
                                        >
                                            <Grid item xs={4}>
                                                <Box
                                                    sx={{ textAlign: "center" }}
                                                >
                                                    <Typography
                                                        variant="h4"
                                                        color="primary"
                                                        fontWeight="bold"
                                                    >
                                                        {/* {(tokenBalance.totals.total_balance + tokenBalance.totals.total_purchased).toLocaleString()} */}
                                                        {tokenBalance.totals.total_purchased.toLocaleString()}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        {t("total_tokens")}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Box
                                                    sx={{ textAlign: "center" }}
                                                >
                                                    <Typography
                                                        variant="h4"
                                                        color="warning.main"
                                                        fontWeight="bold"
                                                    >
                                                        {tokenBalance.totals.total_used.toLocaleString()}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        {t("used_tokens")}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Box
                                                    sx={{ textAlign: "center" }}
                                                >
                                                    <Typography
                                                        variant="h4"
                                                        color="success.main"
                                                        fontWeight="bold"
                                                    >
                                                        {tokenBalance.totals.total_balance.toLocaleString()}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        {t("remaining_tokens")}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                fontWeight="medium"
                                            >
                                                {t("usage_progress")}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                fontWeight="bold"
                                            >
                                                {(() => {
                                                    const total =
                                                        tokenBalance.totals
                                                            .total_balance +
                                                        tokenBalance.totals
                                                            .total_purchased;
                                                    const percentage =
                                                        total > 0
                                                            ? Math.round(
                                                                  (tokenBalance
                                                                      .totals
                                                                      .total_used /
                                                                      total) *
                                                                      100
                                                              )
                                                            : 0;
                                                    return `${percentage}%`;
                                                })()}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                width: "100%",
                                                backgroundColor: "#e0e0e0",
                                                borderRadius: 1,
                                                height: 8,
                                                mt: 1,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: `${(() => {
                                                        const total =
                                                            tokenBalance.totals
                                                                .total_balance +
                                                            tokenBalance.totals
                                                                .total_purchased;
                                                        const percentage =
                                                            total > 0
                                                                ? Math.min(
                                                                      (tokenBalance
                                                                          .totals
                                                                          .total_used /
                                                                          total) *
                                                                          100,
                                                                      100
                                                                  )
                                                                : 0;
                                                        return percentage;
                                                    })()}%`,
                                                    backgroundColor: "#1976d2",
                                                    borderRadius: 1,
                                                    height: 8,
                                                }}
                                            />
                                        </Box>
                                    </>
                                ) : (
                                    <Box sx={{ textAlign: "center", py: 4 }}>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            No token balance data available
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>

                        {/* Service Usage Cards */}
                        {tokenBalance && !tokenBalanceLoading && (
                            <Grid container spacing={3} sx={{ mb: 3 }}>
                                <Grid item xs={3}>
                                    <Card
                                        sx={{
                                            borderLeft: "4px solid #007bff",
                                            boxShadow: "none",
                                            backgroundColor: "#fafbfc",
                                        }}
                                    >
                                        <CardContent sx={{ p: 2 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    mb: 2,
                                                }}
                                            >
                                                <SupportIcon
                                                    sx={{
                                                        color: "#007bff",
                                                        mr: 1,
                                                    }}
                                                />
                                                <Typography
                                                    variant="body1"
                                                    fontWeight="bold"
                                                >
                                                    AI Support
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="h5"
                                                fontWeight="bold"
                                                sx={{ mb: 1 }}
                                            >
                                                {tokenBalance.balances.chat.used.toLocaleString()}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                sx={{ mb: 1 }}
                                            >
                                                tokens
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                color="success.main"
                                                fontWeight="bold"
                                                sx={{ mb: 1 }}
                                            >
                                                $
                                                {(
                                                    tokenBalance.balances.chat
                                                        .used * 0.000275
                                                ).toFixed(2)}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                            >
                                                {t("this_month")}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={3}>
                                    <Card
                                        sx={{
                                            borderLeft: "4px solid #28a745",
                                            boxShadow: "none",
                                            backgroundColor: "#fafbfc",
                                        }}
                                    >
                                        <CardContent sx={{ p: 2 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    mb: 2,
                                                }}
                                            >
                                                <BookingIcon
                                                    sx={{
                                                        color: "#28a745",
                                                        mr: 1,
                                                    }}
                                                />
                                                <Typography
                                                    variant="body1"
                                                    fontWeight="bold"
                                                >
                                                    AI Booking
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="h5"
                                                fontWeight="bold"
                                                sx={{ mb: 1 }}
                                            >
                                                {tokenBalance.balances.booking.used.toLocaleString()}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                sx={{ mb: 1 }}
                                            >
                                                tokens
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                color="success.main"
                                                fontWeight="bold"
                                                sx={{ mb: 1 }}
                                            >
                                                $
                                                {(
                                                    tokenBalance.balances
                                                        .booking.used * 0.00027
                                                ).toFixed(2)}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                            >
                                                {t("this_month")}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={3}>
                                    <Card
                                        sx={{
                                            borderLeft: "4px solid #6f42c1",
                                            boxShadow: "none",
                                            backgroundColor: "#fafbfc",
                                        }}
                                    >
                                        <CardContent sx={{ p: 2 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    mb: 2,
                                                }}
                                            >
                                                <VoiceIcon
                                                    sx={{
                                                        color: "#6f42c1",
                                                        mr: 1,
                                                    }}
                                                />
                                                <Typography
                                                    variant="body1"
                                                    fontWeight="bold"
                                                >
                                                    AI Voice
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="h5"
                                                fontWeight="bold"
                                                sx={{ mb: 1 }}
                                            >
                                                {tokenBalance.balances.call.used.toLocaleString()}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                sx={{ mb: 1 }}
                                            >
                                                tokens
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                color="success.main"
                                                fontWeight="bold"
                                                sx={{ mb: 1 }}
                                            >
                                                $
                                                {(
                                                    tokenBalance.balances.call
                                                        .used * 0.000277
                                                ).toFixed(2)}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                            >
                                                {t("this_month")}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={3}>
                                    <Card
                                        sx={{
                                            borderLeft: "4px solid #fd7e14",
                                            boxShadow: "none",
                                            backgroundColor: "#fafbfc",
                                        }}
                                    >
                                        <CardContent sx={{ p: 2 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    mb: 2,
                                                }}
                                            >
                                                <ChatIcon
                                                    sx={{
                                                        color: "#fd7e14",
                                                        mr: 1,
                                                    }}
                                                />
                                                <Typography
                                                    variant="body1"
                                                    fontWeight="bold"
                                                >
                                                    AI Storage
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="h5"
                                                fontWeight="bold"
                                                sx={{ mb: 1 }}
                                            >
                                                {
                                                    tokenBalance.balances
                                                        .storage.used_formatted
                                                        ?.value
                                                }{" "}
                                                {
                                                    tokenBalance.balances
                                                        .storage.used_formatted
                                                        ?.unit
                                                }
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                sx={{ mb: 1 }}
                                            >
                                                used
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                color="success.main"
                                                fontWeight="bold"
                                                sx={{ mb: 1 }}
                                            >
                                                {
                                                    tokenBalance.balances
                                                        .storage
                                                        .remaining_formatted
                                                        ?.value
                                                }{" "}
                                                {
                                                    tokenBalance.balances
                                                        .storage
                                                        .remaining_formatted
                                                        ?.unit
                                                }{" "}
                                                remaining
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                            >
                                                {t("this_month")}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        )}

                        {/* Total Summary */}
                        {tokenBalance && !tokenBalanceLoading && (
                            <Card
                                sx={{
                                    backgroundColor: "#f8f9fa",
                                    boxShadow: "none",
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{ mb: 1 }}
                                    >
                                        {t("total_summary")}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{ mb: 3 }}
                                    >
                                        {t("total_consumption_description")}
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <Box sx={{ textAlign: "center" }}>
                                                <Typography
                                                    variant="h4"
                                                    color="primary"
                                                    fontWeight="bold"
                                                >
                                                    {tokenBalance.totals.total_used.toLocaleString()}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {t("total_tokens_used")}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ textAlign: "center" }}>
                                                <Typography
                                                    variant="h4"
                                                    color="success.main"
                                                    fontWeight="bold"
                                                >
                                                    $
                                                    {tokenBalance.totals.total_cost.toFixed(
                                                        2
                                                    )}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {t("total_cost")}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Subscription Packages Modal */}
            <Dialog
                open={subscriptionModalOpen}
                onClose={handleSubscriptionModalClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                        overflow: "hidden",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        background:
                            "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                        color: "white",
                        py: 2.5,
                        px: 3,
                        position: "relative",
                        textAlign: "center",
                    }}
                >
                    <Button
                        onClick={handleSubscriptionModalClose}
                        sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            minWidth: "auto",
                            color: "white",
                            "&:hover": {
                                backgroundColor: "rgba(255,255,255,0.1)",
                            },
                        }}
                    >
                        <CloseIcon />
                    </Button>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2,
                        }}
                    >
                        <ElectricIcon sx={{ fontSize: 28 }} />
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: "bold" }}
                            >
                                Choose Subscription Plan
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Select your AI package
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    {subscriptionLoading && (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                py: 8,
                                px: 4,
                            }}
                        >
                            <Box sx={{ position: "relative", mb: 3 }}>
                                <CircularProgress
                                    size={80}
                                    thickness={4}
                                    sx={{
                                        color: "#4f46e5",
                                        "& .MuiCircularProgress-circle": {
                                            strokeLinecap: "round",
                                        },
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        width: 50,
                                        height: 50,
                                        borderRadius: "50%",
                                        backgroundColor: "#4f46e5",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <ElectricIcon
                                        sx={{ color: "white", fontSize: 24 }}
                                    />
                                </Box>
                            </Box>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: "bold", mb: 1 }}
                            >
                                Loading packages...
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Please wait
                            </Typography>
                        </Box>
                    )}

                    {subscriptionError && (
                        <Box sx={{ p: 4 }}>
                            <Alert
                                severity="error"
                                sx={{
                                    borderRadius: 3,
                                    "& .MuiAlert-icon": {
                                        fontSize: 28,
                                    },
                                }}
                                action={
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={fetchSubscriptionPackages}
                                        sx={{ fontWeight: "bold" }}
                                    >
                                        Retry
                                    </Button>
                                }
                            >
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: "bold", mb: 0.5 }}
                                >
                                    Failed to load packages
                                </Typography>
                                <Typography variant="body2">
                                    {subscriptionError}
                                </Typography>
                            </Alert>
                        </Box>
                    )}

                    {!subscriptionLoading && !subscriptionError && (
                        <Box sx={{ p: 3, backgroundColor: "#f8fafc" }}>
                            {/* Customer Info Form */}
                            {/* <Box sx={{ 
                mb: 3, 
                p: 3, 
                backgroundColor: 'white', 
                borderRadius: 2, 
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#374151' }}>
                  Customer Information (Optional)
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  You can input your email at Stripe checkout page, or pre-fill it here
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address (Optional)"
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#4f46e5',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4f46e5',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name (Optional)"
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#4f46e5',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4f46e5',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box> */}

                            <Grid container spacing={2}>
                                {subscriptionPackages.map((pkg) => (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        key={pkg.id}
                                    >
                                        <Card
                                            sx={{
                                                height: "100%",
                                                position: "relative",
                                                borderRadius: 2,
                                                border: pkg.isFeatured
                                                    ? "2px solid #4f46e5"
                                                    : "1px solid #e2e8f0",
                                                boxShadow: pkg.isFeatured
                                                    ? "0 4px 12px rgba(79, 70, 229, 0.15)"
                                                    : "0 2px 8px rgba(0,0,0,0.05)",
                                                transition: "all 0.2s ease",
                                                overflow: "visible",
                                                "&:hover": {
                                                    transform:
                                                        "translateY(-4px)",
                                                    boxShadow: pkg.isFeatured
                                                        ? "0 8px 24px rgba(79, 70, 229, 0.25)"
                                                        : "0 4px 16px rgba(0,0,0,0.1)",
                                                },
                                            }}
                                        >
                                            {pkg.isFeatured && (
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        top: -8,
                                                        right: 8,
                                                        background:
                                                            "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                                                        color: "white",
                                                        px: 1.5,
                                                        py: 0.5,
                                                        borderRadius: 1,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        boxShadow:
                                                            "0 2px 8px rgba(79, 70, 229, 0.3)",
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <StarIcon
                                                        sx={{ fontSize: 12 }}
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        sx={{
                                                            fontSize: "0.7rem",
                                                        }}
                                                    >
                                                        POPULAR
                                                    </Typography>
                                                </Box>
                                            )}

                                            <CardContent
                                                sx={{
                                                    p: 2,
                                                    height: "100%",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        textAlign: "center",
                                                        mb: 2,
                                                        mt: pkg.isFeatured
                                                            ? 1
                                                            : 0,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight="bold"
                                                        sx={{
                                                            mb: 0.5,
                                                            color: pkg.isFeatured
                                                                ? "#4f46e5"
                                                                : "#1e293b",
                                                        }}
                                                    >
                                                        {pkg.name}
                                                    </Typography>
                                                    {pkg.description && (
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                            sx={{
                                                                display:
                                                                    "block",
                                                                mb: 1.5,
                                                                lineHeight: 1.3,
                                                                fontSize:
                                                                    "0.75rem",
                                                            }}
                                                        >
                                                            {pkg.description}
                                                        </Typography>
                                                    )}
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "baseline",
                                                            justifyContent:
                                                                "center",
                                                            gap: 0.5,
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="h5"
                                                            fontWeight="bold"
                                                            sx={{
                                                                color: pkg.isFeatured
                                                                    ? "#4f46e5"
                                                                    : "#1e293b",
                                                            }}
                                                        >
                                                            {pkg.formattedPrice}
                                                        </Typography>
                                                        {/* <Typography variant="caption" color="textSecondary">
                              /{pkg.billingPeriod === 'monthly' ? 'mo' : 'yr'}
                            </Typography> */}
                                                    </Box>
                                                    {pkg.billingPeriod ===
                                                        "yearly" && (
                                                        <Chip
                                                            label="Save 20%"
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    "#fbbf24",
                                                                color: "#92400e",
                                                                fontWeight:
                                                                    "bold",
                                                                fontSize:
                                                                    "0.65rem",
                                                                height: 18,
                                                            }}
                                                        />
                                                    )}
                                                </Box>

                                                <Box sx={{ flex: 1, mb: 2 }}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                        sx={{
                                                            mb: 1.5,
                                                            color: "#374151",
                                                            fontSize: "0.8rem",
                                                        }}
                                                    >
                                                        Features:
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection:
                                                                "column",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <CheckCircleIcon
                                                                sx={{
                                                                    fontSize: 14,
                                                                    color: "#16a34a",
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontSize:
                                                                        "0.75rem",
                                                                }}
                                                            >
                                                                <strong>
                                                                    {
                                                                        pkg
                                                                            .limits
                                                                            .chatTokensFormatted
                                                                    }
                                                                </strong>{" "}
                                                                chat_tokens
                                                            </Typography>
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <CheckCircleIcon
                                                                sx={{
                                                                    fontSize: 14,
                                                                    color: "#2563eb",
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontSize:
                                                                        "0.75rem",
                                                                }}
                                                            >
                                                                <strong>
                                                                    {
                                                                        pkg
                                                                            .limits
                                                                            .callTokensFormatted
                                                                    }
                                                                </strong>{" "}
                                                                call_tokens
                                                            </Typography>
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <CheckCircleIcon
                                                                sx={{
                                                                    fontSize: 14,
                                                                    color: "#d97706",
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontSize:
                                                                        "0.75rem",
                                                                }}
                                                            >
                                                                <strong>
                                                                    {
                                                                        pkg
                                                                            .limits
                                                                            .bookingTokensFormatted
                                                                    }
                                                                </strong>{" "}
                                                                booking_tokens
                                                            </Typography>
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <CheckCircleIcon
                                                                sx={{
                                                                    fontSize: 14,
                                                                    color: "#7c3aed",
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontSize:
                                                                        "0.75rem",
                                                                }}
                                                            >
                                                                <strong>
                                                                    {
                                                                        pkg
                                                                            .limits
                                                                            .storageFormatted
                                                                    }
                                                                </strong>{" "}
                                                                storage
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>

                                                <Button
                                                    variant={
                                                        pkg.isFeatured
                                                            ? "contained"
                                                            : "outlined"
                                                    }
                                                    fullWidth
                                                    size="small"
                                                    onClick={() =>
                                                        handleSubscribe(pkg)
                                                    }
                                                    disabled={paymentLoading}
                                                    sx={{
                                                        py: 1,
                                                        borderRadius: 1.5,
                                                        fontWeight: "bold",
                                                        textTransform: "none",
                                                        fontSize: "0.8rem",
                                                        background:
                                                            pkg.isFeatured
                                                                ? "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)"
                                                                : "transparent",
                                                        border: pkg.isFeatured
                                                            ? "none"
                                                            : "1px solid #4f46e5",
                                                        color: pkg.isFeatured
                                                            ? "white"
                                                            : "#4f46e5",
                                                        boxShadow:
                                                            pkg.isFeatured
                                                                ? "0 2px 8px rgba(79, 70, 229, 0.2)"
                                                                : "none",
                                                        "&:hover": {
                                                            background:
                                                                pkg.isFeatured
                                                                    ? "linear-gradient(135deg, #4338ca 0%, #6d28d9 100%)"
                                                                    : "rgba(79, 70, 229, 0.1)",
                                                            boxShadow:
                                                                pkg.isFeatured
                                                                    ? "0 4px 12px rgba(79, 70, 229, 0.3)"
                                                                    : "none",
                                                        },
                                                        "&:disabled": {
                                                            opacity: 0.6,
                                                            cursor: "not-allowed",
                                                        },
                                                    }}
                                                    startIcon={
                                                        paymentLoading ? (
                                                            <CircularProgress
                                                                size={16}
                                                                color="inherit"
                                                            />
                                                        ) : null
                                                    }
                                                >
                                                    {paymentLoading
                                                        ? "Processing..."
                                                        : "Buy Now"}
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>

                            {subscriptionPackages.length === 0 && (
                                <Box sx={{ textAlign: "center", py: 8 }}>
                                    <ElectricIcon
                                        sx={{
                                            fontSize: 48,
                                            color: "#cbd5e1",
                                            mb: 2,
                                        }}
                                    />
                                    <Typography
                                        variant="h6"
                                        color="textSecondary"
                                        fontWeight="bold"
                                    >
                                        No packages available
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{ mt: 0.5 }}
                                    >
                                        Subscription packages coming soon
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* WhatsApp Configuration Modal */}
            <Dialog
                open={whatsappModalOpen}
                onClose={handleWhatsAppModalClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                        overflow: "hidden",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        background:
                            "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                        color: "white",
                        py: 3,
                        px: 4,
                    }}
                >
                    {/* Refresh */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                            sx={{
                                p: 1,
                                borderRadius: "50%",
                                backgroundColor: "rgba(255,255,255,0.2)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <WhatsAppIcon sx={{ fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: "bold", mb: 0.5 }}
                            >
                                Configure WhatsApp
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Manage your WhatsApp integration settings
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    {whatsappLoading && (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                py: 6,
                                px: 4,
                            }}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    mb: 3,
                                }}
                            >
                                <CircularProgress
                                    size={60}
                                    thickness={4}
                                    sx={{
                                        color: "#25D366",
                                        "& .MuiCircularProgress-circle": {
                                            strokeLinecap: "round",
                                        },
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        width: 40,
                                        height: 40,
                                        borderRadius: "50%",
                                        backgroundColor: "#25D366",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <WhatsAppIcon
                                        sx={{ color: "white", fontSize: 20 }}
                                    />
                                </Box>
                            </Box>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: "bold", mb: 1 }}
                            >
                                Checking Connection
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{ textAlign: "center" }}
                            >
                                Please wait while we verify your WhatsApp
                                connection status...
                            </Typography>
                        </Box>
                    )}

                    {whatsappError && (
                        <Box sx={{ p: 4 }}>
                            <Alert
                                severity="error"
                                sx={{
                                    borderRadius: 2,
                                    "& .MuiAlert-icon": {
                                        fontSize: 24,
                                    },
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: "medium" }}
                                >
                                    Connection Error
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {whatsappError}
                                </Typography>
                            </Alert>
                        </Box>
                    )}

                    {!whatsappLoading && !whatsappError && whatsappStatus && (
                        <Box sx={{ p: 4 }}>
                            {whatsappStatus.connected ? (
                                <Box sx={{ textAlign: "center" }}>
                                    <Box
                                        sx={{
                                            position: "relative",
                                            display: "inline-block",
                                            mb: 3,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 100,
                                                height: 100,
                                                borderRadius: "50%",
                                                background:
                                                    "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxShadow:
                                                    "0 8px 32px rgba(76, 175, 80, 0.3)",
                                                mx: "auto",
                                            }}
                                        >
                                            <CheckCircleIcon
                                                sx={{
                                                    fontSize: 50,
                                                    color: "white",
                                                }}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: -5,
                                                right: -5,
                                                width: 30,
                                                height: 30,
                                                borderRadius: "50%",
                                                backgroundColor: "#25D366",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                border: "3px solid white",
                                                boxShadow:
                                                    "0 2px 8px rgba(0,0,0,0.15)",
                                            }}
                                        >
                                            <WhatsAppIcon
                                                sx={{
                                                    color: "white",
                                                    fontSize: 16,
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: "bold",
                                            mb: 1,
                                            background:
                                                "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
                                            backgroundClip: "text",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                        }}
                                    >
                                        Connected
                                    </Typography>

                                    <Typography
                                        variant="body1"
                                        color="textSecondary"
                                        sx={{
                                            mb: 3,
                                            maxWidth: 300,
                                            mx: "auto",
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        WhatsApp is successfully connected and
                                        ready to use for AI-powered
                                        conversations.
                                    </Typography>
                                </Box>
                            ) : showQr ? (
                                <Box sx={{ textAlign: "center" }}>
                                    <Typography
                                        variant="h5"
                                        sx={{ fontWeight: "bold", mb: 2 }}
                                    >
                                        Scan QR Code
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{ mb: 3 }}
                                    >
                                        Scan this QR code with your WhatsApp
                                        mobile app to connect
                                    </Typography>

                                    <Box
                                        sx={{
                                            position: "relative",
                                            display: "inline-block",
                                        }}
                                    >
                                        {qrCode ? (
                                            <Box sx={{ position: "relative" }}>
                                                <img
                                                    src={qrCode}
                                                    alt="WhatsApp QR Code"
                                                    style={{
                                                        width: 250,
                                                        height: 250,
                                                        borderRadius: 8,
                                                        border: "2px solid #e0e0e0",
                                                    }}
                                                />

                                                {/* WhatsApp-style expired overlay */}
                                                {isQrTimeout && (
                                                    <Box
                                                        sx={{
                                                            position:
                                                                "absolute",
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            backgroundColor:
                                                                "rgba(255, 255, 255, 0.8)",
                                                            borderRadius: 8,
                                                            display: "flex",
                                                            flexDirection:
                                                                "column",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={reloadQrCode}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 60,
                                                                height: 60,
                                                                borderRadius:
                                                                    "50%",
                                                                backgroundColor:
                                                                    "#25D366",
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "center",
                                                                mb: 2,
                                                                boxShadow:
                                                                    "0 4px 12px rgba(37, 211, 102, 0.3)",
                                                            }}
                                                        >
                                                            <SettingsIcon
                                                                sx={{
                                                                    color: "white",
                                                                    fontSize: 28,
                                                                }}
                                                            />
                                                        </Box>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: "#25D366",
                                                                fontWeight:
                                                                    "bold",
                                                                textAlign:
                                                                    "center",
                                                                lineHeight: 1.2,
                                                            }}
                                                        >
                                                            CLICK TO RELOAD
                                                            <br />
                                                            QR CODE
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: 250,
                                                    height: 250,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    backgroundColor: "#f5f5f5",
                                                    borderRadius: 8,
                                                }}
                                            >
                                                <CircularProgress size={40} />
                                            </Box>
                                        )}
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{ mt: 2, fontSize: "12px" }}
                                    >
                                        QR code will automatically refresh if
                                        not scanned within 2 minutes
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: "center" }}>
                                    <Box
                                        sx={{
                                            position: "relative",
                                            display: "inline-block",
                                            mb: 3,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 100,
                                                height: 100,
                                                borderRadius: "50%",
                                                background:
                                                    "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxShadow:
                                                    "0 8px 32px rgba(244, 67, 54, 0.3)",
                                                mx: "auto",
                                            }}
                                        >
                                            <CancelIcon
                                                sx={{
                                                    fontSize: 50,
                                                    color: "white",
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: "bold",
                                            mb: 1,
                                            color: "#f44336",
                                        }}
                                    >
                                        Disconnected
                                    </Typography>

                                    <Typography
                                        variant="body1"
                                        color="textSecondary"
                                        sx={{
                                            mb: 3,
                                            maxWidth: 300,
                                            mx: "auto",
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        WhatsApp is not connected. Please
                                        configure your WhatsApp settings to
                                        enable AI features.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        p: 3,
                        backgroundColor: "#f8f9fa",
                        borderTop: "1px solid #e9ecef",
                    }}
                >
                    <Button
                        onClick={handleWhatsAppModalClose}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            textTransform: "none",
                            fontWeight: "bold",
                            borderColor: "#dee2e6",
                            color: "#6c757d",
                            "&:hover": {
                                borderColor: "#adb5bd",
                                backgroundColor: "#f8f9fa",
                            },
                        }}
                    >
                        Close
                    </Button>
                    <Button
                        onClick={checkWhatsAppStatus}
                        variant="contained"
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            textTransform: "none",
                            fontWeight: "bold",
                            borderColor: "#dee2e6",
                            backgroundColor: "#25D366",
                            color: "white",
                            "&:hover": {
                                borderColor: "#adb5bd",
                                backgroundColor: "#25d365b6",
                            },
                        }}
                    >
                        Refresh
                    </Button>
                    {whatsappStatus && whatsappStatus.connected && (
                        <Button
                            onClick={handleLogoutWhatsApp}
                            variant="contained"
                            disabled={whatsappLoading}
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                py: 1,
                                textTransform: "none",
                                fontWeight: "bold",
                                background:
                                    "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
                                boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
                                "&:hover": {
                                    background:
                                        "linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)",
                                    boxShadow:
                                        "0 6px 16px rgba(244, 67, 54, 0.4)",
                                },
                                "&:disabled": {
                                    background: "#e0e0e0",
                                    color: "#9e9e9e",
                                    boxShadow: "none",
                                },
                            }}
                            startIcon={
                                whatsappLoading ? (
                                    <CircularProgress
                                        size={16}
                                        color="inherit"
                                    />
                                ) : (
                                    <CancelIcon />
                                )
                            }
                        >
                            {whatsappLoading ? "Logging out..." : "Logout"}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AiSettings;
